import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { SessionReplayService } from '@celerofinancas/service-utils';
import { UserState } from '@celerofinancas/state-management';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import {
  applyApplicationTheme,
  isApplicationThemeMessageOriginAllowed,
  resolveApplicationThemeFromMessage,
  resolveApplicationThemeFromQueryParams,
} from '@shared/constants/theme';
import { InMemoryOAuthStorageService } from '@shared/services/in-memory-oauth-storage.service';
import { FeatureFlagService } from '@celerofinancas/ui-feature-flag';
import { Store } from '@ngxs/store';
import { combineLatest } from 'rxjs';

/**
 * Feature flag that controls the Amplitude Session Replay integration in the
 * Caixa Web Component. Toggling it OFF in Flagsmith stops replay capture and
 * prevents the SDK from being initialized. Effective in runtime: existing
 * sessions are gracefully shut down when the flag flips to OFF.
 */
const SESSION_REPLAY_FEATURE_FLAG = 'front@session-replay-event';

/**
 * Root component of the Caixa Web Component application.
 */
@Component({
  selector: 'caixa-webcomponent-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  /**
   * Handles theme updates received from the iframe host.
   */
  @HostListener('window:message', ['$event'])
  public onWindowMessage(event: MessageEvent<unknown>): void {
    const theme = resolveApplicationThemeFromMessage(event.data);

    if (!theme) {
      return;
    }

    if (!isApplicationThemeMessageOriginAllowed(event.origin)) {
      return;
    }

    applyApplicationTheme(theme);
  }

  /**
   * Token handler service for the application.
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * In-memory storage used by the token handler.
   */
  private readonly tokenStorage = inject(InMemoryOAuthStorageService);

  /**
   * Session Replay service used to record Amplitude sessions when the user
   * is authenticated and the feature flag is enabled.
   */
  private readonly sessionReplay = inject(SessionReplayService);

  /**
   * Feature flag service used to gate the Session Replay integration via the
   * `front@session-replay-event` flag in Flagsmith.
   */
  private readonly featureFlag = inject(FeatureFlagService<string>);

  /**
   * NGXS store used to observe the authenticated user state.
   */
  private readonly store = inject(Store);

  /**
   * Destroy ref forwarded to `takeUntilDestroyed` so the Session Replay
   * subscription is cleaned up when the component is torn down.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Tracks whether the Session Replay SDK already booted in the current page
   * so subsequent UserState emissions only refresh identity instead of
   * re-initializing the SDK.
   */
  private sessionReplayBooted = false;

  /**
   * On init lifecycle hook
   */
  public ngOnInit(): void {
    this.tokens.setStorage(this.tokenStorage);
    this.applyThemeFromQueryParams();
    this.bindSessionReplayUser();
  }

  /**
   * Applies the initial theme from the iframe URL query params.
   */
  private applyThemeFromQueryParams(): void {
    const theme = resolveApplicationThemeFromQueryParams(window.location.search);

    if (theme) {
      applyApplicationTheme(theme);
    }
  }

  /**
   * Reactively boots Session Replay when the user is authenticated AND the
   * `front@session-replay-event` feature flag is enabled. The integration
   * uses `user.pk` as deviceId so the value matches the `distinct_id` that
   * the backend forwards to Amplitude (required for event/replay correlation).
   *
   * When the feature flag flips OFF in runtime, the active SDK is gracefully
   * shut down so subsequent tracking events stop carrying replay correlation
   * IDs. When the flag flips back ON with an authenticated user, the SDK
   * boots again with a fresh session.
   */
  private bindSessionReplayUser(): void {
    combineLatest([
      this.store.select(UserState.getState),
      this.featureFlag.isFlagEnabled$(SESSION_REPLAY_FEATURE_FLAG),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, replayEnabled]) => {
        const deviceId = user?.pk ? String(user.pk) : null;

        if (!replayEnabled || !deviceId) {
          if (this.sessionReplayBooted) {
            this.sessionReplay.shutdown();
            this.sessionReplayBooted = false;
          }
          return;
        }

        if (!this.sessionReplayBooted) {
          this.sessionReplayBooted = true;
          this.sessionReplay.init({ deviceId });
          return;
        }

        this.sessionReplay.setUser({
          id: deviceId,
          email: user.email,
          deviceId,
        });
      });
  }
}
